import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Notice,
  Select,
  Spinner,
  Table,
  TableWrap,
  Textarea,
  Toolbar,
  useToast,
} from 'iboon-ui';

import { api, type Asset } from '../api';

/**
 * Things away being fixed.
 *
 * The list worth having is the open one -- what is out of the building right
 * now and who has it -- so that is what this shows by default. The closed ones
 * are there for the question that decides whether to replace something: how
 * many times has this been back, and what has it cost.
 */
export type Repair = {
  id: string;
  fault: string;
  vendor: string;
  cost: number | null;
  resolution: string;
  notes: string;
  sentAt: string;
  returnedAt: string | null;
  openedBy: string;
  closedBy: string;
  assetId: string;
  assetName: string;
  assetTag: string | null;
  serialNumber: string | null;
};

const when = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const days = (from: string, to: string | null): number =>
  Math.max(0, Math.round((new Date(to ?? Date.now()).getTime() - new Date(from).getTime()) / 86_400_000));

export const money = (value: number | null): string =>
  value === null ? '—' : `₹${value.toLocaleString('en-IN')}`;

export function Repairs() {
  const [repairs, setRepairs] = useState<Repair[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState<Repair | null>(null);

  const load = useCallback(async (withClosed: boolean) => {
    const result = await api.get<{ repairs: Repair[] }>(
      `/api/repairs${withClosed ? '?includeClosed=true' : ''}`,
    );
    setRepairs(result.repairs);
  }, []);

  useEffect(() => {
    setRepairs(null);
    load(includeClosed).catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not load repairs.'),
    );
  }, [includeClosed, load]);

  if (error) {
    return (
      <Notice tone="danger" title="Could not load repairs">
        {error}
      </Notice>
    );
  }

  const open = (repairs ?? []).filter((repair) => !repair.returnedAt);

  return (
    <>
      <Toolbar>
        <span className="sub">
          {open.length} {open.length === 1 ? 'thing is' : 'things are'} away
        </span>
        <Button variant="ghost" onClick={() => setIncludeClosed((was) => !was)}>
          {includeClosed ? 'Only what is away' : 'Show finished'}
        </Button>
        <Button variant="primary" onClick={() => setOpening(true)}>
          Send something for repair
        </Button>
      </Toolbar>

      {!repairs ? (
        <Spinner />
      ) : repairs.length === 0 ? (
        <EmptyState
          title={includeClosed ? 'Nothing has ever been repaired' : 'Nothing is away'}
          description="When something goes to the menders, record it here so the register knows where it is."
        />
      ) : (
        <Card padded={false}>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>What</th>
                  <th>What is wrong</th>
                  <th>Who has it</th>
                  <th>Away</th>
                  <th className="num">Cost</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {repairs.map((repair) => (
                  <tr key={repair.id}>
                    <td className="tcell-name">
                      <Link to={`/assets/${repair.assetId}`}>{repair.assetName}</Link>
                      <div className="sub">{repair.assetTag ?? repair.serialNumber ?? '—'}</div>
                    </td>
                    <td className="sub">{repair.fault || '—'}</td>
                    <td className="sub">{repair.vendor || '—'}</td>
                    <td>
                      {repair.returnedAt ? (
                        <span className="sub">
                          {days(repair.sentAt, repair.returnedAt)} days, back {when(repair.returnedAt)}
                        </span>
                      ) : (
                        <Badge tone={days(repair.sentAt, null) > 14 ? 'danger' : 'accent'}>
                          {days(repair.sentAt, null)} days
                        </Badge>
                      )}
                    </td>
                    <td className="num">{money(repair.cost)}</td>
                    <td>
                      <div className="row-actions">
                        {!repair.returnedAt && (
                          <Button variant="ghost" onClick={() => setClosing(repair)}>
                            It came back
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      {opening && (
        <OpenRepair
          onClose={() => setOpening(false)}
          onDone={() => {
            setOpening(false);
            void load(includeClosed);
          }}
        />
      )}

      {closing && (
        <CloseRepair
          repair={closing}
          onClose={() => setClosing(null)}
          onDone={() => {
            setClosing(null);
            void load(includeClosed);
          }}
        />
      )}
    </>
  );
}

function OpenRepair({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetId, setAssetId] = useState('');
  const [fault, setFault] = useState('');
  const [vendor, setVendor] = useState('');
  const [cost, setCost] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api
      .get<{ assets: Asset[] }>('/api/assets')
      // Retired things cannot go for repair, and offering them means a refusal
      // after the form is filled in rather than before.
      .then((r) => {
        const candidates = r.assets.filter((a) => a.state !== 'retired' && a.state !== 'repair');
        setAssets(candidates);
        setAssetId(candidates[0]?.id ?? '');
      })
      .catch(() => setAssets([]));
  }, []);

  async function save() {
    setBusy(true);
    try {
      await api.post('/api/repairs', { assetId, fault, vendor, cost });
      toast.show('Recorded. It is marked as away being fixed.');
      onDone();
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not record it.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="sm" title="Send something for repair" onClose={onClose}>
      {assets.length === 0 ? (
        <Notice tone="accent" title="Nothing to send">
          Everything is either already away or retired.
        </Notice>
      ) : (
        <>
          <Field label="What is going" required>
            <Select value={assetId} onChange={(event) => setAssetId(event.target.value)}>
              {assets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                  {asset.assetTag ? ` — ${asset.assetTag}` : ''}
                  {asset.heldByName ? ` (${asset.heldByName} has it)` : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="What is wrong" required>
            <Textarea rows={2} value={fault} onChange={(event) => setFault(event.target.value)} />
          </Field>
          <Field label="Who is fixing it">
            <Input value={vendor} onChange={(event) => setVendor(event.target.value)} placeholder="Lenovo service centre" />
          </Field>
          <Field label="What it will cost" hint="Leave blank until you know. Blank is not zero.">
            <Input type="number" value={cost} onChange={(event) => setCost(event.target.value)} />
          </Field>

          <Notice tone="accent" title="This takes it off whoever has it">
            The register cannot say somebody has a laptop that is at the menders, so their entry in
            its history is closed.
          </Notice>

          <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
            <Button variant="primary" disabled={busy || !assetId || !fault.trim()} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Send it'}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

function CloseRepair({
  repair,
  onClose,
  onDone,
}: {
  repair: Repair;
  onClose: () => void;
  onDone: () => void;
}) {
  const [outcome, setOutcome] = useState('fixed');
  const [resolution, setResolution] = useState('');
  const [cost, setCost] = useState(repair.cost === null ? '' : String(repair.cost));
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function save() {
    setBusy(true);
    try {
      await api.post(`/api/repairs/${repair.id}/close`, { outcome, resolution, cost });
      toast.show(
        outcome === 'fixed'
          ? `${repair.assetName} is back on the shelf.`
          : `${repair.assetName} is retired.`,
      );
      onDone();
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not close it.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="sm" title={`${repair.assetName} came back`} onClose={onClose}>
      <Field label="How did it come back" required>
        <Select value={outcome} onChange={(event) => setOutcome(event.target.value)}>
          <option value="fixed">Working — put it back on the shelf</option>
          <option value="unfixable">Beyond repair — retire it</option>
        </Select>
      </Field>
      <Field label="What was done">
        <Textarea rows={2} value={resolution} onChange={(event) => setResolution(event.target.value)} />
      </Field>
      <Field label="What it cost">
        <Input type="number" value={cost} onChange={(event) => setCost(event.target.value)} />
      </Field>

      {/* It does not go back to whoever had it before. Said, because somebody
          expecting that would otherwise think the handover was lost. */}
      {outcome === 'fixed' && (
        <Notice tone="accent" title="It goes back on the shelf">
          Not back to whoever had it before — give it to somebody from the register when it is
          needed.
        </Notice>
      )}

      <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
        <Button variant="primary" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Close it'}
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
